// ============================================================
// App — Komposition, Routing, Empty-States
// ============================================================

import { useEffect, useMemo } from 'react';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { DetailDrawer } from './components/DetailDrawer';
import { Toast } from './components/Toast';
import { useApp, useEntries } from './store';
import { inRange, period } from './aggregator';
import { EmptyFirstStart, NoHits } from './views/EmptyStates';
import { Uebersicht } from './views/Uebersicht';
import { Zeitverlauf } from './views/Zeitverlauf';
import { Spielarten } from './views/Spielarten';
import { Muster } from './views/Muster';
import { Einzelbuchungen } from './views/Einzelbuchungen';
import { Datenimport } from './views/Datenimport';

export function App() {
  const { history, route, setDetailId, periodMode, customFrom, customTo, compare } = useApp();
  const entries = useEntries();

  const p = useMemo(() => period(periodMode, entries, customFrom, customTo), [periodMode, entries, customFrom, customTo]);
  const filtered = useMemo(
    () => (p.full ? entries.slice() : entries.filter((e) => inRange(e, p))),
    [entries, p],
  );

  // ESC schließt Detail-Drawer
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDetailId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setDetailId]);

  const hasData = entries.length > 0;

  // Wenn keine Daten und nicht auf Import-Seite → EmptyFirstStart
  if (!hasData && route !== 'datenimport') {
    return (
      <div style={{ display: 'flex', height: '100vh', minWidth: 1180, background: 'var(--bg)' }}>
        <Sidebar />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Header filteredCount={0} />
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '26px 32px 60px' }}>
            <EmptyFirstStart />
          </div>
        </div>
        <DetailDrawer />
        <Toast />
      </div>
    );
  }

  // Bestimmen, ob "noHits" gezeigt werden soll:
  // Wenn Vergleich an UND aktueller Zeitraum leer → noHits.
  // Wenn Eintragungen existieren, der aktuelle Zeitraum aber leer ist,
  // zeigen wir nur auf Übersicht/Zeitverlauf/Spielarten den NoHits.
  const noHits = !p.full && filtered.length === 0 && route !== 'datenimport' && route !== 'einzelbuchungen';

  return (
    <div style={{ display: 'flex', height: '100vh', minWidth: 1180, background: 'var(--bg)' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Header filteredCount={filtered.length} />
        <div
          key={`${route}`}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '26px 32px 60px',
            animation: 'wlFade .22s ease',
          }}
        >
          {noHits ? <NoHits /> : renderRoute(route)}
        </div>
      </div>
      <DetailDrawer />
      <Toast />
    </div>
  );
}

function renderRoute(route: string) {
  switch (route) {
    case 'uebersicht':
      return <Uebersicht />;
    case 'zeitverlauf':
      return <Zeitverlauf />;
    case 'spielarten':
      return <Spielarten />;
    case 'muster':
      return <Muster />;
    case 'einzelbuchungen':
      return <Einzelbuchungen />;
    case 'datenimport':
      return <Datenimport />;
    default:
      return null;
  }
}
